import { useContext, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { AuthContext } from '../../context/AuthContext';
import { Nav, Card, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import {
	FaHome, FaCog, FaListAlt, FaLightbulb, FaThList, FaClone,
	FaChevronLeft, FaSignOutAlt, FaQuestionCircle
} from 'react-icons/fa';
import logoDark from '../../assets/logo-dark.png';
import { getUserRole } from '../../utils/authUtils';
import { getRoleRank } from '../../constants/roles';
import DashboardsNavItem from './DashboardsNavItem';
import useTemplateSuggestionCount from '../../hooks/useTemplateSuggestionCount';

const defaultNavItems = [
	// Rendered as the Dashboards dropdown (DashboardsNavItem) instead of a plain link.
	{ path: '/home',            label: 'Dashboards',      icon: FaHome, dropdown: true },
	// Owner/Admin only - View Only users cannot manage dashboards (the route is guarded too).
	{ path: '/manage-dashboards', label: 'Manage Dashboards', icon: FaThList, minRank: 3 },
	// Owner/Admin only - View Only users cannot create agents (the route is guarded too).
	{ path: '/templates',       label: 'Templates',       icon: FaClone, minRank: 3 },
	// Email Log and Client Insights show raw email content (subjects, bodies). Every endpoint they
	// depend on is Owner/Admin/SuperAdmin-only, so both are hidden from View Only users here and
	// their routes are guarded the same way in App.jsx (a pasted URL must not reach them either).
	{ path: '/email-log',       label: 'Email Log',       icon: FaListAlt, minRank: 3 },
	{ path: '/client-insights', label: 'Client Insights', icon: FaLightbulb, minRank: 3 },
	{ path: '/settings',        label: 'Settings',        icon: FaCog },
];

// Six white squares (2×3 grid) shown when the sidebar is collapsed.
const SixDotsIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
		<rect x="1"  y="1" width="4" height="6" rx="1" fill="white" />
		<rect x="6"  y="1" width="4" height="6" rx="1" fill="white" />
		<rect x="11" y="1" width="4" height="6" rx="1" fill="white" />
		<rect x="1"  y="9" width="4" height="6" rx="1" fill="white" />
		<rect x="6"  y="9" width="4" height="6" rx="1" fill="white" />
		<rect x="11" y="9" width="4" height="6" rx="1" fill="white" />
	</svg>
);

const SideBarComponent = ({ navItems: propNavItems, onLogout }) => {
	const navigate = useNavigate();
	const location = useLocation();

	const {
		state,
		actions: { toggleSidebar, toggleUserGuide }
	} = useContext(ThemeContext);
	const { state: authState } = useContext(AuthContext);

	const isCollapsed = state.sidebarCollapsed;

	// minRank gates an item to roles at or above that privilege rank (1=SuperAdmin is most
	// privileged, 4=User/View Only is least) — see defaultNavItems' Client Insights entry.
	// Unauthenticated/unresolved role defaults to rank 99 (most restrictive) rather than showing
	// gated items during the brief hydration window before authState.user loads.
	const userRank = authState.user ? getRoleRank(getUserRole(authState.user)) : 99;
	const navItems = (propNavItems ?? defaultNavItems).filter(item => !item.minRank || userRank <= item.minRank);

	// Unmatched email that fits a template is suggested on the Templates page; the badge says how many
	// are waiting. Owners/Admins of a client only: SuperAdmins use the admin menu, which has no client.
	const showSuggestionBadge = !propNavItems && userRank >= 2 && userRank <= 3;
	const suggestionCount = useTemplateSuggestionCount(showSuggestionBadge);

	const handleNavigation = (path) => navigate(path);
	const isActive = (path) => location.pathname === path;

	const navLinkBase = {
		cursor: 'pointer',
		borderRadius: 0,
		transition: 'all 0.2s ease',
	};

	return (
		<div
			className={`sidebar-container bg-light border-end shadow-sm ${isCollapsed ? 'collapsed' : ''}`}
			style={{
				width: isCollapsed ? '56px' : '220px',
				height: '100vh',
				position: 'fixed',
				top: 0,
				left: 0,
				zIndex: 1000,
				transition: 'width 0.3s ease'
			}}
		>
			<Card className="h-100 border-0 rounded-0">
				<Card.Header
					className="sidebar-header text-white border-0 py-3 d-flex align-items-center justify-content-center"
					style={{ minHeight: '56px' }}
				>
					{isCollapsed ? (
						<SixDotsIcon />
					) : (
						<img
							src={logoDark}
							alt="Alert Panel"
							style={{
								height: '28px',
								maxWidth: '160px',
								objectFit: 'contain',
								objectPosition: 'center center',
							}}
						/>
					)}
				</Card.Header>

				<Card.Body className="d-flex flex-column p-0">
					<Nav className="flex-column flex-grow-1">
						{navItems.map((item) => {
							if (item.dropdown) {
								return <DashboardsNavItem key={item.path} isCollapsed={isCollapsed} />;
							}
							const IconComponent = item.icon;
							const active = isActive(item.path);
							return (
								<Nav.Item key={item.path}>
									<Nav.Link
										onClick={() => handleNavigation(item.path)}
										className={`d-flex align-items-center px-3 py-3 border-0 ${
											active ? 'sidebar-nav-active text-white fw-bold' : 'text-dark hover-bg-light'
										}`}
										style={navLinkBase}
									>
										<IconComponent size={16} className={isCollapsed ? 'mx-auto' : 'me-3'} />
										{!isCollapsed && <span className="fw-medium">{item.label}</span>}
										{item.path === '/templates' && suggestionCount > 0 && (
											<Badge
												bg="danger"
												pill
												className={`sidebar-badge${isCollapsed ? ' sidebar-badge--collapsed' : ' ms-auto'}`}
												title={`${suggestionCount} template ${suggestionCount === 1 ? 'suggestion' : 'suggestions'} waiting`}
												aria-label={`${suggestionCount} template ${suggestionCount === 1 ? 'suggestion' : 'suggestions'} waiting`}
											>
												{suggestionCount}
											</Badge>
										)}
									</Nav.Link>
								</Nav.Item>
							);
						})}
						{/* User Guide — toggles the sliding help drawer */}
						<Nav.Item>
							<Nav.Link
								onClick={() => toggleUserGuide()}
								title="User Guide"
								className={`d-flex align-items-center px-3 py-3 border-0 ${
									state.userGuideOpen ? 'sidebar-nav-active text-white fw-bold' : 'text-dark hover-bg-light'
								}`}
								style={navLinkBase}
							>
								<FaQuestionCircle size={16} className={isCollapsed ? 'mx-auto' : 'me-3'} />
								{!isCollapsed && <span className="fw-medium">User Guide</span>}
							</Nav.Link>
						</Nav.Item>
						{/* Logout button — shown when an onLogout handler is provided (e.g. admin panel) */}
						{onLogout && (
							<Nav.Item className="mt-auto">
								<Nav.Link
									onClick={onLogout}
									title="Sign out"
									className="d-flex align-items-center px-3 py-3 border-0 text-danger hover-bg-light"
									style={navLinkBase}
								>
									<FaSignOutAlt size={16} className={isCollapsed ? 'mx-auto' : 'me-3'} />
									{!isCollapsed && <span className="fw-medium">Sign Out</span>}
								</Nav.Link>
							</Nav.Item>
						)}
						{/* Collapse toggle — bottom of nav, aligned with nav icons */}
						<Nav.Item>
							<Nav.Link
								onClick={() => toggleSidebar()}
								title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
								className="d-flex align-items-center px-3 py-3 border-0 text-dark hover-bg-light border-bottom"
								style={navLinkBase}
							>
								{isCollapsed
									? <FaChevronLeft size={14} className="mx-auto" style={{ transform: 'rotate(180deg)' }} />
									: <>
										<FaChevronLeft size={14} className="me-3" />
										<span className="fw-medium">Collapse</span>
									</>
								}
							</Nav.Link>
						</Nav.Item>
					</Nav>
				</Card.Body>
			</Card>
		</div>
	);
};

SideBarComponent.propTypes = {
	navItems: PropTypes.arrayOf(
		PropTypes.shape({
			path: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			icon: PropTypes.elementType.isRequired,
			dropdown: PropTypes.bool,
		})
	),
	onLogout: PropTypes.func,
};

const SideBar = memo(SideBarComponent);
export default SideBar;
